import { AppContext } from '@/ui/components/Context';
import { datadogLogs } from '@datadog/browser-logs';
import { GlobalLoader } from '@juvare/common-web-ui/GlobalLoader';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { getErrorMessage } from '@juvare/common-web-ui/utils';
import { registerConfigurableListLoader } from '@juvare/platform-sdk-react/ui/features/configurable-lists';
import { useEditablePages } from '@juvare/platform-sdk-react/ui/features/editable-pages';
import { usePublicSettingsLoader, useSettingsLoader } from '@juvare/platform-sdk-react/ui/features/editable-settings';
import { useAppManifestLoader, usePlatformLoader } from '@juvare/platform-sdk-react/ui/hooks';
import { isUserLoggedIn } from '@juvare/platform-sdk-react/ui/stores';
import { combineLoadingStatuses } from '@juvare/platform-sdk-react/ui/utils';
import { Alert } from '@mui/material';
import React from 'react';
import { useSelector } from 'react-redux';
import { Anonymous } from './Anonymous';
import { UserApp } from './UserApp';
import { useEnvironmentConfig } from './hooks/useEnvironmentConfig';
import { appName } from './utils/utils';

registerConfigurableListLoader(appName, 'configurableList').catch(handleErrorSnackbar);

export const App: React.FC = () => {
    const status = combineLoadingStatuses([
        useAppManifestLoader(),
        usePlatformLoader({ appContext: AppContext }),
        usePublicSettingsLoader(),
        useSettingsLoader(),
    ]);
    const userLoggedIn = useSelector(isUserLoggedIn);
    const { data: environmentConfig, error: environmentConfigError } = useEnvironmentConfig();
    const pages = useEditablePages();
    React.useEffect(() => {
        if (environmentConfig && environmentConfig?.datadogClientToken) {
            datadogLogs.init({
                clientToken: environmentConfig.datadogClientToken,
                forwardErrorsToLogs: true,
                service: `HAN_${appName}`,
                env: environmentConfig.environment,
                version: window.appVersion,
                silentMultipleInit: true,
            });
        }
    }, [environmentConfig]);

    if (!pages || !environmentConfig) {
        return <GlobalLoader />;
    } else if (environmentConfigError) {
        return <Alert security='error'>{getErrorMessage(environmentConfigError)}</Alert>;
    } else if (status === 'done') {
        return userLoggedIn ? <UserApp pages={pages} config={environmentConfig} /> : <Anonymous config={environmentConfig} pages={pages} />;
    }
    return <GlobalLoader />;
};
